import React, { useEffect, useRef, useState } from "react";
import { FaEraser, FaUpload } from "react-icons/fa6";
import { MdInsertChart } from "react-icons/md";
import axios from "axios";

const PWIUpdate = () => {
  const inputRefs = useRef([]);
  const urlPrefix = process.env.REACT_APP_URL_PREFIX;

  /** ------------------ STATIC DATA ------------------ **/
  const [pwiData, setPwiData] = useState([]);
  const sections = [
    "MKP/ALD",
    "BINA",
    "ET",
    "MDVK",
    "NKJ/BSP",
    "MHDA/DHN",
    "AGD",
    "OHAN",
    "KEQ",
    "KTKD",
  ];

  const rakeTypes = ["JUMBO", "BOXN"];

  const fields = [
    "to_value",
    "ho_value",
    "to_loaded",
    "to_empty",
    "ho_loaded",
    "ho_empty",
    "to_intensive",
    "to_nonintensive",
  ];

  const buildRowsFromPwi = (data) => {
    return sections.flatMap((section) =>
      rakeTypes.map((type) => {
        const found = data.find(
          (item) => item.section === section && item.rake_type === type
        );
        return {
          section,
          rake_type: type,
          id: found?.id ?? "",
          to_value: found?.to_value ?? "",
          ho_value: found?.ho_value ?? "",
          to_loaded: found?.to_loaded ?? "",
          to_empty: found?.to_empty ?? "",
          ho_loaded: found?.ho_loaded ?? "",
          ho_empty: found?.ho_empty ?? "",
          to_intensive: found?.to_intensive ?? "",
          to_nonintensive: found?.to_nonintensive ?? "",
        };
      })
    );
  };

  const createInitialRows = () =>
    sections.flatMap((section) =>
      rakeTypes.map((type) => {
        const row = { section, rake_type: type };
        fields.forEach((f) => (row[f] = ""));
        return row;
      })
    );

  const [reportDate, setReportDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );

  const [rows, setRows] = useState(() => {
    const tData = pwiData.filter(
      (d) => d.interchange_date === new Date().toISOString().slice(0, 10)
    );
    return buildRowsFromPwi(tData);
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${urlPrefix}getPWIDatewise?date=${reportDate}`
      );
      if (response.data.status === "ok") {
        setPwiData(response.data.data);
      }
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch stations:", error);
      setLoading(false);
      setError(true);
    }
  };
  useEffect(() => {
    fetchData();
  }, [reportDate]);

  useEffect(() => {
    const filtered = pwiData.filter(
      (d) => d.interchange_date.slice(0, 10) === reportDate
    );
    setRows(buildRowsFromPwi(filtered));
  }, [reportDate, pwiData]);

  const handleInputChange = (index, field, value) => {
    const updated = [...rows];
    const { section, rake_type } = updated[index];

    if (field === "to_value" || field === "ho_value") {
      updated.forEach((row) => {
        if (row.section === section) row[field] = value;
      });
    } else {
      updated[index][field] = value;
    }

    setRows(updated);
  };

  const handleSubmit = async () => {
    const updatedRows = rows.map((row) => ({
      ...row,
      interchange_date: reportDate,
    }));

    try {
      const response = await fetch(`${urlPrefix}pwiupdate`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedRows),
      });

      if (!response.ok) {
        alert("Please check date and try again.");
        throw new Error("Network response was not ok");
      }

      await response.json();
      setRows(createInitialRows());
      alert("success");
      setReportDate(new Date().toISOString().slice(0, 10));
    } catch (err) {
      console.error("Submission error:", err);
      setError(true);
    }
  };

  const calsum = () => {
    const sums = { JUMBO: {}, BOXN: {} };
    rakeTypes.forEach((tp) => {
      fields.forEach((fd) => {
        let sum = 0;
        rows.forEach((row) => {
          if (row.rake_type === tp) sum += Number(row[fd]) || 0;
        });
        sums[tp][fd] = sum;
      });
    });
    return sums;
  };

  const rakeTypeSums = calsum();
  return (
    <div className="container">
      <div className="row text-center papaDiv">
        <h5 className="p-2">
          <MdInsertChart />
          Point-Wise Inter Change Update
        </h5>
        <h5>
          Update data in <b style={{ color: "red" }}>"PWI"</b> table
        </h5>
      </div>

      <div className="papaDiv">
        <div className="d-flex align-items-center justify-content-center m-1">
          <label className="me-2 fw-bold">Select Date:</label>
          <input
            type="date"
            className="form-control w-auto"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
          />
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead className="table-dark">
              <tr>
                <th>Sno.</th>
                <th>Section</th>
                <th>Rake Type</th>
                {fields.map((field) => (
                  <th key={field}>
                    {(field.charAt(0).toUpperCase() + field.slice(1)).replace(
                      "_",
                      " "
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {rowIndex % 2 === 0 && (
                    <td rowSpan={2} className="text-center">
                      {rowIndex / 2 + 1}
                    </td>
                  )}
                  {rowIndex % 2 === 0 && (
                    <td className="text-center" rowSpan={2}>
                      {row.section}
                    </td>
                  )}
                  <td>{row.rake_type}</td>

                  {fields.map((field, colIndex) => {
                    const inputIndex = `${rowIndex}-${colIndex}`;
                    return (
                      <td key={field}>
                        <input
                          style={{
                            height: "26px",
                            padding: "2px 6px",
                            fontSize: "0.8rem",
                          }}
                          type="number"
                          className="form-control"
                          value={row[field] ?? ""}
                          onChange={(e) =>
                            handleInputChange(
                              rowIndex,
                              field,
                              Number(e.target.value)
                            )
                          }
                          disabled={
                            (field === "to_value" || field === "ho_value") &&
                            rowIndex % 2 === 1
                          }
                          ref={(el) => {
                            inputRefs.current[inputIndex] = el;
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === "ArrowDown") {
                              e.preventDefault();
                              const nextRow = rowIndex + 2;
                              const nextInput =
                                inputRefs.current[`${nextRow}-${colIndex}`];
                              if (nextInput) nextInput.focus();
                            }
                            if (e.key === "ArrowUp") {
                              e.preventDefault();
                              const prevRow = rowIndex - 2;
                              const prevInput =
                                inputRefs.current[`${prevRow}-${colIndex}`];
                              if (prevInput) prevInput.focus();
                            }
                            if (e.key === "ArrowRight") {
                              e.preventDefault();
                              const nextCol = colIndex + 1;
                              const nextInput =
                                inputRefs.current[`${rowIndex}-${nextCol}`];
                              if (nextInput) nextInput.focus();
                            }
                            if (e.key === "ArrowLeft") {
                              e.preventDefault();
                              const prevCol = colIndex - 1;
                              const prevInput =
                                inputRefs.current[`${rowIndex}-${prevCol}`];
                              if (prevInput) prevInput.focus();
                            }
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="table-responsive-sm">
          <table className="table table-bordered table-striped">
            <thead className="table-light">
              <tr>
                <th
                  colSpan={12}
                  className="fs-4 fw-semibold border bg-dark text-white text-center"
                >
                  Total
                </th>
              </tr>
              <tr>
                <th>Sr no.</th>
                <th>Rake Type</th>
                {fields.map((field) => (
                  <th key={field}>
                    {(field.charAt(0).toUpperCase() + field.slice(1)).replace(
                      "_",
                      " "
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rakeTypes.map((type, idx) => (
                <tr key={type}>
                  <td className="fw-bold">{idx + 1}</td>
                  <td className="fw-bold">{type}</td>
                  {fields.map((field) => (
                    <td key={field}>{rakeTypeSums[type][field]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-center">
          <button
            className="btn btn-outline-danger px-5 me-2"
            onClick={() => setRows(createInitialRows())}
          >
            <FaEraser /> Clear All
          </button>
          <button
            className="btn btn-outline-success px-5"
            onClick={handleSubmit}
          >
            <FaUpload /> Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWIUpdate;
